import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { LogoutConfirmationService } from '../../services/logout-confirmation.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  unreadCount: number = 0;
  logoUrl: string | null = null;
  private subscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  constructor(
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly logoutConfirmationService: LogoutConfirmationService
  ) {}

  ngOnInit(): void {
    // Get logo URL from localStorage
    this.logoUrl = localStorage.getItem('logo_url');
    
    // Subscribe to unread count changes
    this.subscription = this.notificationService.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });

    // Load unread count once on component initialization
    this.loadUnreadCount();

    // Subscribe to router navigation events to refresh count on menu switching
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.loadUnreadCount();
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Load unread count from API
   */
  async loadUnreadCount(): Promise<void> {
    try {
      const countObservable = await this.notificationService.getUnreadCount(true);
      countObservable.subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.notificationService.updateUnreadCount(response.data.unread_count);
          }
        },
        error: (error) => {
          console.error('Error loading unread count:', error);
        }
      });
    } catch (error) {
      console.error('Error getting unread count:', error);
    }
  }

  /**
   * Navigate to notifications page based on current module
   */
  navigateToNotifications(): void {
    const currentUrl = this.router.url;

    // Extract module from URL (first segment after base URL)
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);

    if (urlSegments.length > 0) {
      const module = urlSegments[0]; // First segment is the module (user, corporate, ngo, volunteer)
      const notificationsRoute = `/${module}/notifications`;

      this.router.navigate([notificationsRoute]);
    } else {
      console.error('Unable to determine current module from URL');
    }
  }

  /**
   * Get module type based on current route
   */
  getModuleType(): string {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);

    if (urlSegments.length > 0) {
      const module = urlSegments[0]; // First segment is the module (user, corporate, ngo, volunteer)

      switch (module.toLowerCase()) {
        case 'user':
          return 'Admin';
        case 'corporate':
          return 'Corporate';
        case 'ngo':
          return 'NGO';
        case 'volunteer':
          return 'Volunteer';
        default:
          return 'Unknown';
      }
    }

    return 'Unknown';
  }
  
  /**
   * Check if current module is Corporate
   */
  isCorporateModule(): boolean {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);
    
    if (urlSegments.length > 0) {
      return urlSegments[0].toLowerCase() === 'corporate';
    }
    
    return false;
  }

  /**
   * Check if profile navigation is clickable (only for Corporate and NGO, not Admin)
   */
  isProfileClickable(): boolean {
    const moduleType = this.getModuleType();
    return moduleType === 'Corporate' || moduleType === 'NGO';
  }

  /**
   * Navigate to profile page based on current module
   */
  navigateToProfile(): void {
    if (!this.isProfileClickable()) {
      return; // Do nothing for Admin
    }

    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);

    if (urlSegments.length > 0) {
      const module = urlSegments[0]; // First segment is the module (corporate, ngo)
      
      if (module.toLowerCase() === 'corporate') {
        this.router.navigate(['/corporate/organization-profile']);
      } else if (module.toLowerCase() === 'ngo') {
        this.router.navigate(['/ngo/profile']);
      }
    }
  }

  /**
   * Show logout confirmation modal
   */
  logout(): void {
    this.logoutConfirmationService.showLogoutModal();
  }
}
